import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import FillableFormFiller from "@/components/FillableFormFiller";
import { useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";
import { PenTool, Loader2, Stethoscope, Bell, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MANAGERS = ["admin", "case_manager", "hr_scheduler", "scheduler"];

function RequestRow({ r, isManager, canSign, rns, onSign, onChanged }) {
  const [busy, setBusy] = useState(false);
  const reassign = async (rnId) => {
    setBusy(true);
    try {
      await axios.post(`${API}/cosign-requests/${r.id}/reassign`, { rn_id: rnId === "any" ? null : rnId });
      toast.success(rnId === "any" ? "Open to any RN" : "RN reassigned and notified");
      onChanged();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not reassign"); } finally { setBusy(false); }
  };
  const nudge = async () => {
    setBusy(true);
    try { await axios.post(`${API}/cosign-requests/${r.id}/nudge`); toast.success("Reminder sent"); onChanged(); }
    catch (err) { toast.error(err.response?.data?.detail || "Could not send reminder"); } finally { setBusy(false); }
  };
  const age = Math.floor((Date.now() - new Date(r.created_at).getTime()) / 86400000);
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-200 bg-white p-4" data-testid={`cosign-row-${r.id}`}>
      <div className="flex-1 min-w-[220px]">
        <p className="font-medium text-slate-900">{r.filename}</p>
        <p className="text-xs text-slate-500">
          {r.employee_name} · submitted {new Date(r.created_at).toLocaleDateString()}
          {r.status === "pending" && age >= 2 && <span className="ml-2 text-amber-700">· waiting {age} days</span>}
          {r.status === "signed" && <span className="ml-2 text-emerald-700">· signed by {r.signed_by_name} {new Date(r.signed_at).toLocaleDateString()}</span>}
        </p>
      </div>
      {r.status === "pending" ? (
        <>
          {isManager ? (
            <Select value={r.assigned_rn_id || "any"} onValueChange={reassign} disabled={busy}>
              <SelectTrigger className="h-8 w-48 text-xs" data-testid={`cosign-rn-select-${r.id}`}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Any RN</SelectItem>
                {rns.map((n) => <SelectItem key={n.id} value={n.id}>{n.name}</SelectItem>)}
              </SelectContent>
            </Select>
          ) : (
            <Badge variant="outline" className="text-xs">{r.assigned_rn_name ? `Assigned to ${r.assigned_rn_name}` : "Any RN"}</Badge>
          )}
          {isManager && (
            <Button size="sm" variant="outline" onClick={nudge} disabled={busy} className="gap-1" data-testid={`cosign-nudge-${r.id}`}>
              <Bell className="w-3.5 h-3.5" /> Remind
            </Button>
          )}
          {canSign && (
            <Button size="sm" onClick={() => onSign(r)} className="gap-1 bg-rose-600 hover:bg-rose-700" data-testid={`cosign-sign-${r.id}`}>
              <PenTool className="w-3.5 h-3.5" /> Review &amp; sign
            </Button>
          )}
        </>
      ) : (
        <Badge className="bg-emerald-100 text-emerald-800 gap-1"><CheckCircle2 className="w-3 h-3" /> Signed</Badge>
      )}
    </div>
  );
}

export default function RnSignaturesPage() {
  const { user } = useAuth();
  const role = (user?.role || "").toLowerCase();
  const isManager = MANAGERS.includes(role);
  const canSign = role === "rn" || role === "admin";
  const [status, setStatus] = useState("pending");
  const [items, setItems] = useState(null);
  const [rns, setRns] = useState([]);
  const [signing, setSigning] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/cosign-requests`, { params: { status } });
      setItems(r.data.requests);
    } catch (err) { toast.error(err.response?.data?.detail || "Could not load requests"); setItems([]); }
  }, [status]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (isManager) axios.get(`${API}/cosign-requests/meta/rns`).then((r) => setRns(r.data.rns)).catch(() => {}); }, [isManager]);

  const openSign = async (r) => {
    try {
      const d = await axios.get(`${API}/cosign-requests/${r.id}`);
      setSigning(d.data);
    } catch (err) { toast.error(err.response?.data?.detail || "Could not open form"); }
  };

  const submitSign = async (values) => {
    await axios.post(`${API}/cosign-requests/${signing.id}/sign`, { values });
    toast.success("Signed — the completed form is now in the employee's file");
    setSigning(null);
    load();
    window.dispatchEvent(new Event("cosign-changed"));
  };

  return (
    <Layout>
    <div className="p-6 max-w-5xl mx-auto space-y-6" data-testid="rn-signatures-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><Stethoscope className="w-6 h-6 text-rose-600" /> RN Signatures</h1>
          <p className="text-sm text-slate-500">Forms completed by staff that need a nurse's co-signature.</p>
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-40" data-testid="cosign-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="pending">Awaiting signature</SelectItem>
            <SelectItem value="signed">Signed</SelectItem>
            <SelectItem value="all">All</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {items === null ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 p-12 text-center text-slate-500" data-testid="cosign-empty">
          Nothing waiting for a signature.
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((r) => <RequestRow key={r.id} r={r} isManager={isManager} canSign={canSign} rns={rns} onSign={openSign} onChanged={load} />)}
        </div>
      )}
      {signing && (
        <FillableFormFiller
          open={!!signing}
          onClose={() => setSigning(null)}
          form={signing}
          pdfEndpoint={`${API}/cosign-requests/${signing.id}/pdf`}
          onSubmitValues={submitSign}
          submitLabel="Sign & finalize"
        />
      )}
    </div>
    </Layout>
  );
}
